/**
 * O campo `contact` do chamado guarda coisas diferentes conforme o canal.
 *
 * Num chamado de e-mail ele é o nome de quem escreveu. Num da URA ele é o
 * TELEFONE de quem ligou -- a ligação chega sem nenhum nome, e o telefone é a
 * única identificação que existe naquele momento.
 *
 * O cadastro rápido de contato preenchia o campo Nome com esse valor, sem
 * distinguir os dois casos. O resultado era uma lista de contatos chamados
 * "5519997654321" e, pior, a impressão de cadastro pronto: o operador via um
 * nome no campo e não percebia que ninguém sabia quem era a pessoa.
 *
 * Pedido da Gisele (item 2): vindo da URA, o Nome nasce em branco, pedindo
 * para ser preenchido.
 */

/**
 * Só dígitos e a pontuação de telefone -- nada que sirva como nome.
 *
 * O `\s` aqui dentro é o que faz espaço em volta ser inofensivo: sem ele,
 * " 19 99765-4321 " deixaria de casar e voltaria a virar nome.
 */
const SO_TELEFONE = /^[\d\s()+.-]+$/;

/**
 * O valor é um telefone, e não um nome?
 *
 * Deliberadamente conservador: na dúvida, é NOME. Errar para este lado apenas
 * repete o comportamento antigo (o campo vem preenchido e o operador corrige);
 * errar para o outro apagaria um nome que o operador já tinha.
 */
export const pareceTelefone = (valor) => {
    const texto = String(valor || '');

    // Precisa ter dígito. Cobre dois casos de uma vez: "()" e "--", que casam
    // com a classe acima e não são telefone nenhum, e o campo VAZIO, que não é
    // telefone nem nome.
    if (!/\d/.test(texto)) return false;

    return SO_TELEFONE.test(texto);
};

/**
 * Separa o `contact` do chamado nas duas coisas que ele pode carregar.
 *
 * ## Por que ele vem junto, e por que aqui ele se separa
 *
 * Desde 19/10/2026 o chamado nascido do WhatsApp grava
 * `"Fulano - (19) 99765-4321"` no campo `contact` -- decisao explicita do dono
 * do produto, e ela continua valendo: ali os dois juntos sao o que serve a
 * quem le o chamado uma semana depois. O nome sozinho nao diz para onde ligar
 * de volta, e o numero sozinho nao diz com quem se falou.
 *
 * O CADASTRO de contato e outro lugar: ali nome e telefone tem colunas
 * proprias (`clients.name` e `clients.whatsapp_user_number`). Jogar a string
 * inteira no Nome produzia contatos chamados "Fulano - (19) 99765-4321" e o
 * campo de telefone vazio -- ponto 14 da Gisele (20/10): "ao finalizar o
 * atendimento, gravar cada um no seu campo".
 *
 * ## O corte e no ULTIMO " - ", e so se o que vem depois for telefone
 *
 * Nome com traco existe ("Maria - Financeiro"), e cortar nele perderia metade
 * do nome. `pareceTelefone` ja e deliberadamente conservador: na duvida, e
 * nome. Errar para esse lado repete o comportamento antigo, que o operador
 * corrige; errar para o outro apaga informacao que ninguem vai reparar que
 * sumiu.
 */
const partesDoContato = (contactDoChamado) => {
    const texto = String(contactDoChamado || '').trim();

    if (texto === '') return { nome: '', telefone: '' };

    // So o telefone: a ligacao da URA chega sem nome nenhum.
    if (pareceTelefone(texto)) return { nome: '', telefone: texto };

    const corte = texto.lastIndexOf(' - ');

    if (corte === -1) return { nome: texto, telefone: '' };

    const sufixo = texto.slice(corte + 3).trim();

    return pareceTelefone(sufixo)
        ? { nome: texto.slice(0, corte).trim(), telefone: sufixo }
        : { nome: texto, telefone: '' };
};

/**
 * O que vai no campo Nome ao abrir o cadastro rápido de contato.
 *
 * Em branco quando o `contact` do chamado é só um telefone, e sem o sufixo de
 * telefone quando ele traz os dois.
 */
export const nomeInicialDoContato = (contactDoChamado) => partesDoContato(contactDoChamado).nome;

/** O telefone que vai no campo WhatsApp do mesmo cadastro, quando houver. */
export const telefoneInicialDoContato = (contactDoChamado) => partesDoContato(contactDoChamado).telefone;
